import React, { useEffect, useState } from 'react';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import TextField from 'material-ui/TextField';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import LinearProgress from 'material-ui/LinearProgress';
import Checkbox from 'material-ui/Checkbox';
import { useQuery } from 'react-query';
import {
    applyCorpusComposition,
    createCorpusFromComposition,
    listCoverageMetrics,
    proposeCorpusComposition,
} from 'services/testRuns';

let rowKey = 1;

function nextKey() {
    rowKey += 1;
    return rowKey;
}

function metricLabel(metrics, id) {
    const found = (metrics || []).find(metric => metric.id === id);
    return found ? found.label : id;
}

function formatPercent(item) {
    if (!item || item.percent === null || item.percent === undefined) {
        return '—';
    }
    return `${item.percent}% (${item.covered}/${item.total})`;
}

function emptyObjective(metric) {
    return { key: nextKey(), metric: metric || 'feature', target: 'max', percent: '' };
}

function objectivesFromFill(stored) {
    if (!stored || !Array.isArray(stored.objectives) || stored.objectives.length === 0) {
        return [emptyObjective('feature')];
    }
    return stored.objectives.map(row => ({
        key: nextKey(),
        metric: row.metric,
        target: row.target,
        percent: row.percent === undefined || row.percent === null ? '' : String(row.percent),
    }));
}

function constraintsFromFill(stored) {
    return ((stored && stored.constraints) || []).map(row => ({
        key: nextKey(),
        kind: row.op === 'documents' || row.op === 'clarified' ? row.op : 'metric',
        metric: row.metric || 'feature',
        bound: row.bound || 'atLeast',
        percent: row.percent === undefined || row.percent === null ? '' : String(row.percent),
        count: row.count === undefined || row.count === null ? '' : String(row.count),
        field: row.field || '',
        exists: row.exists === false ? 'absent' : 'exists',
    }));
}

function annotationFromFill(stored) {
    const types = stored && stored.annotationTypes;
    if (types && types.length === 1 && (types[0] === 'cargo' || types[0] === 'vessel')) {
        return types[0];
    }
    return 'all';
}

function fillBody(universe, annotationType, objectives, constraints) {
    const fill = { universe, objectives, constraints };
    if (annotationType === 'cargo' || annotationType === 'vessel') {
        fill.annotationTypes = [annotationType];
    }
    return fill;
}

function MailList({ title, rows, empty }) {
    return (
        <div>
            <h4>{title} ({rows.length})</h4>
            {rows.length === 0 ? <p className="test-runs-hint">{empty}</p> : (
                <ul className="bbr-proposal-list">
                    {rows.map(row => (
                        <li key={row.key}>
                            {row.subject || '—'}
                            {row.detail ? ` · ${row.detail}` : ''}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function ProposeCompositionDialog({
    open,
    corpusId,
    fill,
    onClose,
    onApplied,
    onCreated,
}) {
    const metricsQuery = useQuery('bbr-coverage-metrics', listCoverageMetrics, { enabled: open });
    const metrics = Array.isArray(metricsQuery.data) ? metricsQuery.data : [];
    const [objectives, setObjectives] = useState([emptyObjective('feature')]);
    const [constraints, setConstraints] = useState([]);
    const [proposal, setProposal] = useState(null);
    const [name, setName] = useState('');
    const [annotationType, setAnnotationType] = useState('all');
    const [universe, setUniverse] = useState('full');
    const [pendingFill, setPendingFill] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        setObjectives(objectivesFromFill(fill));
        setConstraints(constraintsFromFill(fill));
        setProposal(null);
        setName('');
        setAnnotationType(annotationFromFill(fill));
        setUniverse(fill && fill.universe === 'reduced' ? 'reduced' : 'full');
        setPendingFill(null);
        setError('');
        setBusy(false);
    }, [open, fill]);

    const close = () => {
        if (!busy) {
            onClose();
        }
    };

    const updateObjective = (key, patch) => {
        setObjectives(current => current.map(row => (row.key === key ? { ...row, ...patch } : row)));
    };

    const moveObjective = (index, delta) => {
        setObjectives(current => {
            const next = current.slice();
            const target = index + delta;
            if (target < 0 || target >= next.length) {
                return current;
            }
            const [row] = next.splice(index, 1);
            next.splice(target, 0, row);
            return next;
        });
    };

    const updateConstraint = (key, patch) => {
        setConstraints(current => current.map(row => (row.key === key ? { ...row, ...patch } : row)));
    };

    const compute = async () => {
        setError('');
        setProposal(null);
        const objectiveBody = [];
        for (const row of objectives) {
            const item = { metric: row.metric, target: row.target };
            if (row.percent !== '') {
                const percent = Number(row.percent);
                if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
                    setError('Objective percent must be from 0 to 100');
                    return;
                }
                item.percent = percent;
            }
            objectiveBody.push(item);
        }
        const constraintBody = [];
        for (const row of constraints) {
            if (row.kind === 'clarified') {
                const field = row.field.trim();
                if (!field) {
                    setError('Clarified field is required');
                    return;
                }
                constraintBody.push({ op: 'clarified', field, exists: row.exists === 'exists' });
                continue;
            }
            if (row.kind === 'documents') {
                const count = Number(row.count);
                if (row.count === '' || !Number.isInteger(count) || count < 0) {
                    setError('Document count must be a whole number from 0 up');
                    return;
                }
                constraintBody.push({ op: 'documents', bound: row.bound, count });
                continue;
            }
            const percent = Number(row.percent);
            if (row.percent === '' || !Number.isFinite(percent) || percent < 0 || percent > 100) {
                setError('Constraint percent must be from 0 to 100');
                return;
            }
            constraintBody.push({
                op: 'metric',
                metric: row.metric,
                bound: row.bound,
                percent,
            });
        }
        const body = {
            objectives: objectiveBody,
            constraints: constraintBody,
            universe,
        };
        if (corpusId) {
            body.corpusId = corpusId;
        }
        if (annotationType === 'cargo' || annotationType === 'vessel') {
            body.annotationTypes = [annotationType];
        }
        const nextFill = fillBody(universe, annotationType, objectiveBody, constraintBody);
        setBusy(true);
        try {
            setProposal(await proposeCorpusComposition(body));
            setPendingFill(nextFill);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const apply = async () => {
        if (!proposal || !corpusId) {
            return;
        }
        setBusy(true);
        setError('');
        try {
            await applyCorpusComposition(corpusId, {
                addCoverageMailIds: proposal.add.map(row => row.coverageMailId),
                removeDocIds: proposal.remove.map(row => row.docId),
                ...(pendingFill ? { fill: pendingFill } : {}),
            });
            if (onApplied) {
                onApplied();
            }
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const saveAsNew = async () => {
        if (!proposal) {
            return;
        }
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Name is required');
            return;
        }
        const keepDocIds = [
            ...(proposal.keep || []).map(row => row.docId),
            ...(proposal.pinned || []).map(row => row.docId),
        ];
        setBusy(true);
        setError('');
        try {
            const created = await createCorpusFromComposition({
                name: trimmed,
                sourceCorpusId: corpusId && keepDocIds.length > 0 ? corpusId : undefined,
                keepDocIds,
                addCoverageMailIds: proposal.add.map(row => row.coverageMailId),
                ...(pendingFill ? { fill: pendingFill } : {}),
            });
            if (onCreated) {
                onCreated(created);
            }
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const targetFull = (proposal && proposal.target && proposal.target.full) || [];
    const targetReduced = (proposal && proposal.target && proposal.target.reduced) || [];
    const currentFull = (proposal && proposal.current && proposal.current.full) || [];
    const currentReduced = (proposal && proposal.current && proposal.current.reduced) || [];

    return (
        <Dialog
            title={corpusId ? 'Propose corpus changes' : 'New corpus from coverage'}
            open={open}
            onRequestClose={close}
            autoScrollBodyContent
            contentStyle={{ width: '92%', maxWidth: 980 }}
            actions={[
                <div key="footer" className="bbr-proposal-footer">
                    {proposal ? (
                        <div className="bbr-proposal-footer-create">
                            <TextField
                                floatingLabelText="Corpus name"
                                value={name}
                                onChange={(e, value) => setName(value)}
                                disabled={busy}
                                style={{ width: 240 }}
                            />
                            {corpusId ? (
                                <RaisedButton
                                    label="Apply to this corpus"
                                    primary
                                    disabled={busy}
                                    onClick={apply}
                                />
                            ) : null}
                            <RaisedButton
                                label={corpusId ? 'Save as new corpus' : 'Create corpus'}
                                primary={!corpusId}
                                disabled={busy || !name.trim()}
                                onClick={saveAsNew}
                            />
                        </div>
                    ) : null}
                    <FlatButton label={busy ? 'Working…' : 'Cancel'} onClick={close} disabled={busy} />
                </div>,
            ]}
        >
            <p className="test-runs-hint">
                Objectives run in order. A max percent is met once coverage reaches it, and later objectives
                keep it at least that high. A min percent is met once coverage is at or below it.
                A blank percent means 100 for max and 0 for min.
            </p>
            <SelectField
                floatingLabelText="Documents"
                value={annotationType}
                onChange={(e, i, value) => setAnnotationType(value)}
                disabled={busy}
                style={{ width: 240 }}
            >
                <MenuItem value="all" primaryText="Cargo and vessel" />
                <MenuItem value="cargo" primaryText="Cargo only" />
                <MenuItem value="vessel" primaryText="Vessel only" />
            </SelectField>
            <div className="bbr-proposal-universe">
                <Checkbox
                    label="Solve against the full set"
                    checked={universe !== 'reduced'}
                    onCheck={(e, checked) => setUniverse(checked ? 'full' : 'reduced')}
                    disabled={busy}
                />
                <p className="test-runs-hint">
                    Checked counts every feature. Unchecked counts features for the selected document types.
                </p>
            </div>
            {objectives.map((row, index) => (
                <div key={row.key} className="bbr-proposal-row">
                    <SelectField
                        floatingLabelText="Score"
                        value={row.metric}
                        onChange={(e, i, value) => updateObjective(row.key, { metric: value })}
                        disabled={busy}
                        style={{ width: 220, marginRight: 12 }}
                    >
                        {metrics.map(metric => (
                            <MenuItem key={metric.id} value={metric.id} primaryText={metric.label} />
                        ))}
                    </SelectField>
                    <SelectField
                        floatingLabelText="Target"
                        value={row.target}
                        onChange={(e, i, value) => updateObjective(row.key, { target: value })}
                        disabled={busy}
                        style={{ width: 120, marginRight: 12 }}
                    >
                        <MenuItem value="max" primaryText="MAX" />
                        <MenuItem value="min" primaryText="MIN" />
                    </SelectField>
                    <TextField
                        floatingLabelText="Percent"
                        hintText={row.target === 'min' ? '0' : '100'}
                        value={row.percent}
                        onChange={(e, value) => updateObjective(row.key, { percent: value })}
                        disabled={busy}
                        style={{ width: 100, marginRight: 8 }}
                    />
                    <FlatButton label="Up" disabled={busy || index === 0} onClick={() => moveObjective(index, -1)} />
                    <FlatButton
                        label="Down"
                        disabled={busy || index === objectives.length - 1}
                        onClick={() => moveObjective(index, 1)}
                    />
                    <FlatButton
                        label="Remove"
                        disabled={busy || objectives.length === 1}
                        onClick={() => setObjectives(current => current.filter(item => item.key !== row.key))}
                    />
                </div>
            ))}
            <FlatButton
                label="Add objective"
                disabled={busy}
                onClick={() => setObjectives(current => current.concat(emptyObjective(metrics[0] && metrics[0].id)))}
            />
            <h4>Constraints</h4>
            {constraints.length === 0 ? (
                <p className="test-runs-hint">No constraints. Add a score floor or cap, a document count, or a clarified key that must exist or not.</p>
            ) : null}
            {constraints.map(row => (
                <div key={row.key} className="bbr-proposal-row bbr-proposal-constraint">
                    <SelectField
                        floatingLabelText="Kind"
                        value={row.kind}
                        onChange={(e, i, value) => updateConstraint(row.key, { kind: value })}
                        disabled={busy}
                        style={{ width: 160 }}
                    >
                        <MenuItem value="metric" primaryText="Score" />
                        <MenuItem value="documents" primaryText="Documents" />
                        <MenuItem value="clarified" primaryText="Clarified" />
                    </SelectField>
                    {row.kind === 'documents' ? (
                        <div className="bbr-proposal-constraint-fields">
                            <SelectField
                                floatingLabelText="Bound"
                                value={row.bound}
                                onChange={(e, i, value) => updateConstraint(row.key, { bound: value })}
                                disabled={busy}
                                style={{ width: 140 }}
                            >
                                <MenuItem value="atLeast" primaryText="at least" />
                                <MenuItem value="atMost" primaryText="at most" />
                            </SelectField>
                            <TextField
                                floatingLabelText="Count"
                                value={row.count}
                                onChange={(e, value) => updateConstraint(row.key, { count: value })}
                                disabled={busy}
                                style={{ width: 100 }}
                            />
                        </div>
                    ) : row.kind === 'metric' ? (
                        <div className="bbr-proposal-constraint-fields">
                            <SelectField
                                floatingLabelText="Score"
                                value={row.metric}
                                onChange={(e, i, value) => updateConstraint(row.key, { metric: value })}
                                disabled={busy}
                                style={{ width: 220 }}
                            >
                                {metrics.map(metric => (
                                    <MenuItem key={metric.id} value={metric.id} primaryText={metric.label} />
                                ))}
                            </SelectField>
                            <SelectField
                                floatingLabelText="Bound"
                                value={row.bound}
                                onChange={(e, i, value) => updateConstraint(row.key, { bound: value })}
                                disabled={busy}
                                style={{ width: 140 }}
                            >
                                <MenuItem value="atLeast" primaryText="at least" />
                                <MenuItem value="atMost" primaryText="at most" />
                            </SelectField>
                            <TextField
                                floatingLabelText="Percent"
                                value={row.percent}
                                onChange={(e, value) => updateConstraint(row.key, { percent: value })}
                                disabled={busy}
                                style={{ width: 100 }}
                            />
                        </div>
                    ) : (
                        <div className="bbr-proposal-constraint-fields">
                            <TextField
                                floatingLabelText="Field"
                                hintText="isNosa"
                                value={row.field}
                                onChange={(e, value) => updateConstraint(row.key, { field: value })}
                                disabled={busy}
                                style={{ width: 160 }}
                            />
                            <SelectField
                                floatingLabelText="Presence"
                                value={row.exists}
                                onChange={(e, i, value) => updateConstraint(row.key, { exists: value })}
                                disabled={busy}
                                style={{ width: 180 }}
                            >
                                <MenuItem value="exists" primaryText="exists" />
                                <MenuItem value="absent" primaryText="does not exist" />
                            </SelectField>
                        </div>
                    )}
                    <FlatButton
                        label="Remove"
                        disabled={busy}
                        onClick={() => setConstraints(current => current.filter(item => item.key !== row.key))}
                    />
                </div>
            ))}
            <div>
                <FlatButton
                    label="Add constraint"
                    disabled={busy}
                    onClick={() => setConstraints(current => current.concat({
                        key: nextKey(),
                        kind: 'metric',
                        metric: (metrics[0] && metrics[0].id) || 'feature',
                        bound: 'atLeast',
                        percent: '',
                        count: '',
                        field: '',
                        exists: 'absent',
                    }))}
                />
            </div>
            <div className="bbr-proposal-actions">
                <RaisedButton label="Compute proposal" primary disabled={busy || objectives.length === 0} onClick={compute} />
            </div>
            {busy ? <LinearProgress /> : null}
            {error ? <div className="test-runs-error">{error}</div> : null}
            {proposal ? (
                <div>
                    {proposal.approximate ? (
                        <p className="test-runs-error">
                            This composition is approximate.
                        </p>
                    ) : null}
                    <table className="bbr-score-table">
                        <thead>
                            <tr>
                                <th>Score</th>
                                <th className={proposal.universe === 'full' ? 'bbr-score-active' : ''}>Current full</th>
                                <th className={proposal.universe === 'reduced' ? 'bbr-score-active' : ''}>Current reduced</th>
                                <th className={proposal.universe === 'full' ? 'bbr-score-active' : ''}>Target full</th>
                                <th className={proposal.universe === 'reduced' ? 'bbr-score-active' : ''}>Target reduced</th>
                            </tr>
                        </thead>
                        <tbody>
                            {targetFull.map(item => {
                                const current = currentFull.find(score => score.metric === item.metric);
                                const currentScoped = currentReduced.find(score => score.metric === item.metric);
                                const targetScoped = targetReduced.find(score => score.metric === item.metric);
                                return (
                                    <tr key={item.metric}>
                                        <td>{metricLabel(metrics, item.metric)}</td>
                                        <td className={proposal.universe === 'full' ? 'bbr-score-active' : ''}>
                                            {proposal.current ? formatPercent(current) : '—'}
                                        </td>
                                        <td className={proposal.universe === 'reduced' ? 'bbr-score-active' : ''}>
                                            {proposal.current ? formatPercent(currentScoped) : '—'}
                                        </td>
                                        <td className={proposal.universe === 'full' ? 'bbr-score-active' : ''}>{formatPercent(item)}</td>
                                        <td className={proposal.universe === 'reduced' ? 'bbr-score-active' : ''}>{formatPercent(targetScoped)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <p className="test-runs-hint">
                        Objectives use the {proposal.universe === 'reduced' ? 'reduced' : 'full'} set.
                        {proposal.annotationTypes && proposal.annotationTypes.length === 1
                            ? ` ${proposal.annotationTypes[0] === 'cargo' ? 'Cargo' : 'Vessel'} documents only.`
                            : ''}
                        {' '}
                        Documents: {proposal.current ? proposal.current.mailCount : 0} current, {proposal.target.mailCount} target
                    </p>
                    {(proposal.objectives || []).map((objective, index) => (
                        <div key={`${objective.metric}-${index}`} className={objective.met ? '' : 'bbr-proposal-unmet'}>
                            {metricLabel(metrics, objective.metric)} {objective.target === 'max' ? 'MAX' : 'MIN'} {objective.percent}% — {objective.met ? 'met' : 'unmet'}
                        </div>
                    ))}
                    {(proposal.constraints || []).map((constraint, index) => (
                        <div key={`constraint-${index}`} className={constraint.met ? '' : 'bbr-proposal-unmet'}>
                            {constraint.op === 'metric'
                                ? `${metricLabel(metrics, constraint.metric)} ${constraint.bound === 'atLeast' ? 'at least' : 'at most'} ${constraint.percent}% — ${constraint.met ? 'met' : 'unmet'}`
                                : constraint.op === 'documents'
                                    ? `Documents ${constraint.bound === 'atLeast' ? 'at least' : 'at most'} ${constraint.count} — ${constraint.met ? 'met' : 'unmet'}`
                                    : `${constraint.field} ${constraint.exists ? 'exists' : 'does not exist'}`}
                        </div>
                    ))}
                    <div className="bbr-proposal-columns">
                        <MailList
                            title="Add"
                            empty="No documents to add."
                            rows={(proposal.add || []).map(row => ({
                                key: row.coverageMailId,
                                subject: row.subject,
                                detail: `${row.annotationType} · ${row.outcome}`,
                            }))}
                        />
                        <MailList
                            title="Remove"
                            empty="No documents to remove."
                            rows={(proposal.remove || []).map(row => ({
                                key: row.docId,
                                subject: row.subject,
                                detail: row.annotationType,
                            }))}
                        />
                        <MailList
                            title="Not in coverage"
                            empty="Every current document is in the coverage index."
                            rows={(proposal.pinned || []).map(row => ({
                                key: row.docId,
                                subject: row.subject,
                                detail: row.annotationType,
                            }))}
                        />
                    </div>
                </div>
            ) : null}
        </Dialog>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/ProposeCompositionDialog.jsx